import React, { useContext } from 'react';

// Styles
import styles from './CreatePollForm.module.css';

// Services
import { getPollFrequency } from '../../../services/Poll';
import { parseReminderTime } from '../../../services/util';

// Hooks
import useInput from '../../../hooks/useInput';

// Components
import Input from '../../UI/Input/Input';
import HourSelector from '../../UI/HourSelector/HourSelector';
import SliderCheckbox from '../../UI/SliderCheckbox/SliderCheckbox';
import MultiSelector from '../../UI/MultiSelector/MultiSelector';

// Interfaces
import { Poll, PollFrequency } from '../../../interfaces/Poll';
import { HH, mm } from '../../../interfaces/Time';
import { PollDispatch } from '../../../interfaces/Reducers';

// Language
import { useTranslation } from 'react-i18next';
import FiltersContext from '../../../context/FiltersContext';
import CircleSelector from '../../UI/CircleSelector/CircleSelector';
import SelectFrequency from '../../Helpers/SelectFrequency/SelectFrequency';
import TitlePollForm from '../TitlePollForm/TitlePollForm';

const CreatePollForm: React.FC<{
    inputErrorIDs: string[]
    poll: Poll
    dispatchPoll: React.Dispatch<PollDispatch>
}> = ({ inputErrorIDs, poll, dispatchPoll }) => {

    const { t } = useTranslation();

    const { profiles } = useContext(FiltersContext);


    const { value: name, valueChangeHandler: handleOnChangeText, inputBlurHandler } = useInput(undefined, poll.name);
    const { value: description, valueChangeHandler: onChangeDescription } = useInput(() => true, poll.description);

    const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatchPoll({ type: 'ADD_NAME', payload: event.target.value });
        handleOnChangeText(event);
    };

    const handleChangeDesciption = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        dispatchPoll({ type: 'ADD_DESCRIPTION', payload: event.target.value });
        onChangeDescription(event);
    };

    const handleChangeValidity = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.trim() === '' ? 24 : event.target.value;
        const parsedValue = Number.isNaN(Number(value)) ? 24 : Number(value);
        dispatchPoll({ type: 'CHANGE_VALIDITY_DURATION', payload: parsedValue })
    }

    const handleChangeReminder = (hours: HH | null, minutes: mm | null) => {
        const value = parseReminderTime(hours, minutes);
        dispatchPoll({ type: 'ADD_REMINDER_TIME', payload: value });
    };

    const handleChangeFreq = (freq: PollFrequency) => dispatchPoll({ type: 'ADD_FREQUENCY', payload: freq });

    const handleChangeHasTables = (evt: React.ChangeEvent<HTMLInputElement>) => {
        dispatchPoll({ type: 'HAS_TABLE', payload: evt.target.checked });
    };

    const handleChangeShowChart = (evt: React.ChangeEvent<HTMLInputElement>) => {
        dispatchPoll({ type: 'SHOW_IN_CHARTS', payload: evt.target.checked });
    };

    // const handleChangeSetAutomaticCall = (evt: React.ChangeEvent<HTMLInputElement>) => {
    //     dispatchPoll({ type: 'SET_AUTOMATIC_CALL', payload: evt.target.checked });
    // };

    const handleChangeType = (value: boolean) => {
        dispatchPoll({ type: 'CHANGE_TYPE', payload: value })
    }

    const handleAddProfile = (id: number | string) => {
        if (typeof id === 'string') return;
        if (id === -1) dispatchPoll({ type: 'SET_PROFILES', payload: profiles.map(profile => profile.id) });
        else dispatchPoll({ type: 'ADD_PROFILE', payload: id });
    };


    const profilesOptions = profiles.map(profile => ({
        id: profile.id,
        name: profile.name || '',
        checked: poll.profile_ids?.includes(profile.id) || false
    }));

    const errorName = inputErrorIDs.includes('poll_name') && (poll.name === undefined || poll.name.trim() === '');
    const errorProfile = inputErrorIDs.includes('selector_profile') &&
        (poll.profile_ids === null || poll.profile_ids === undefined || poll.profile_ids?.length === 0);



    return (
        <div className={styles.pollForm}>
            <div className={styles.flexContainer}>
                <div className={styles.containerInput}>
                    <TitlePollForm>{t('poll_name')}:</TitlePollForm>
                    <div className={styles.inputWrapper}>
                        <Input
                            id={'poll_name'}
                            defaultValue={name}
                            onChange={handleChangeName}
                            onBlur={inputBlurHandler}
                            error={errorName}
                            errorMsg={errorName ? t('name_required') : ''}
                        />
                    </div>
                </div>

            </div>
            <div className={styles.flexContainer}>
                <div className={styles.containerInput}>
                    <TitlePollForm>{t('response_interval_hours')}:</TitlePollForm>
                    <div className={styles.inputWrapper}>
                        <Input
                            type='number'
                            defaultValue={poll.validity_duration_hours}
                            onChange={handleChangeValidity}
                        />
                    </div>
                </div>
                <div className={styles.containerInput}>
                    <TitlePollForm>{t('periodicity')}:</TitlePollForm>
                    <div className={styles.inputWrapper}>
                        <SelectFrequency
                            pollFrequency={getPollFrequency(poll)}
                            handleChangeFreq={handleChangeFreq}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.flexContainer}>
                <div className={styles.containerInput}>
                    <TitlePollForm>{t('associate_to_environment')}:</TitlePollForm>
                    <div className={styles.inputWrapper}>
                        <MultiSelector
                            id={'selector_profile'}
                            options={profilesOptions}
                            handleChange={handleAddProfile}
                            error={errorProfile}
                            errorMsg={errorProfile ? t('atleast_one_environment') as string : ''}
                        />
                    </div>
                </div>
                <div className={styles.containerInput}>
                    <TitlePollForm>{t('reminder')}:</TitlePollForm>
                    <div className={styles.inputWrapper}>
                        <HourSelector
                            hours={poll.reminder_time ? poll.reminder_time.slice(0, 2) as HH : null}
                            minutes={poll.reminder_time ? poll.reminder_time.slice(3, 5) as mm : null}
                            handleChange={handleChangeReminder}
                        />
                    </div>
                </div>
                {window.config.DIFFERENCE_TESTPROM_POLLS &&
                    <div className={styles.containerInput}>
                        <TitlePollForm>{t('type_poll')}:</TitlePollForm>
                        <div className={styles.inputWrapper}>
                            <CircleSelector
                                text='TEST'
                                active={poll.is_profesional_inquiry === true}
                                handleClick={() => handleChangeType(true)}
                            />
                            <CircleSelector
                                text='PROM'
                                active={poll.is_profesional_inquiry === false}
                                handleClick={() => handleChangeType(false)}
                            />
                        </div>
                    </div>
                }
            </div>
            <div className={styles.flexContainer}>
                <div className={styles.containerInput}>
                    <TitlePollForm>{t('show_in_tables')}:</TitlePollForm>
                    <div className={styles.inputWrapper}>
                        <SliderCheckbox
                            label=''
                            handleChange={handleChangeHasTables}
                            defaultChecked={poll.has_table}
                        />
                    </div>
                </div>
                <div className={styles.containerInput}>
                    <TitlePollForm>{t('show_in_charts')}:</TitlePollForm>
                    <div className={styles.inputWrapper}>
                        <SliderCheckbox
                            label=''
                            handleChange={handleChangeShowChart}
                            defaultChecked={poll.show_in_charts}
                        />
                    </div>
                </div>
                {/* <div className={styles.containerInput}>
                    <TitlePollForm>{t('automatic_call')}:</TitlePollForm>
                    <div className={styles.inputWrapper}>
                        <SliderCheckbox
                            label=''
                            handleChange={handleChangeSetAutomaticCall}
                            defaultChecked={poll.auto_call_enabled}
                        />
                    </div>
                </div> */}
            </div>
            <div className={styles.textAreaContainer}>
                <TitlePollForm>{t('poll_description')}:</TitlePollForm>
                <div className={styles.inputWrapper}>
                    <textarea
                        defaultValue={description}
                        onChange={handleChangeDesciption}
                    />

                        </div>
                    </div>
            </div>
    );
};

export default CreatePollForm;